<script lang="ts">
	import { modals } from 'svelte-modals'
	import Checkbox from '../atoms/Checkbox.svelte'
	import { t } from '$i18n/i18n'
	import { fly } from 'svelte/transition'
	import SecondaryButton, { SecondaryBtnColors } from '#lib/atoms/SecondaryButton.svelte'
	import PrimaryButton, { PrimaryBtnColors } from '#lib/atoms/PrimaryButton.svelte'
	import { onMount } from 'svelte'
	import { DefaultRobotoFontProps, type FontProps } from '$types/fontProps'

	export interface CheckboxSelectionItem {
		id: string
		label: string
		prechecked: boolean
	}

	const checkboxLabelFontProps: FontProps = DefaultRobotoFontProps('1.25rem', '0.875rem')

	interface Props {
		/** provided by svelte-modals via modals.open(...) */
		isOpen: boolean
		title: string
		message?: string
		tableHeader?: string
		/** Suffix rendered after the selected count, e.g. "ausgewählt" → "(3) ausgewählt" */
		selectedCountSuffix?: string
		items?: CheckboxSelectionItem[]
		acceptText?: string
		maxWidth?: string
		/** Called when the accept button is clicked. Receives the IDs of all selected items. */
		onConfirm?: (selectedIds: string[]) => void
	}

	let {
		isOpen,
		title,
		message = '',
		tableHeader = '',
		selectedCountSuffix = 'ausgewählt',
		items = [],
		acceptText = '',
		maxWidth = '550px',
		onConfirm = () => {}
	}: Props = $props()

	let selectedIds = $state<string[]>([])
	let selectedCount = $derived(selectedIds.length)

	function toggleItem(id: string, checked: boolean) {
		if (checked) {
			selectedIds = [...selectedIds, id]
		} else {
			selectedIds = selectedIds.filter((i) => i !== id)
		}
	}

	function handleAccept() {
		onConfirm(selectedIds)
		modals.close()
	}

	function preCheck() {
		for (const item of items) {
			if (item.prechecked) {
				selectedIds = [...selectedIds, item.id]
			}
		}
	}

	onMount(() => {
		preCheck()
	})
</script>

{#if isOpen}
	<div
		role="dialog"
		class="modal"
		in:fly={{ y: -100, duration: 600 }}
		out:fly={{ y: -200, duration: 600 }}
	>
		<div class="contents" style="padding: 24px; max-width: {maxWidth}">
			<div class="actual-content">
				<div class="header">{title}</div>
				{#if message && message !== ''}
					<div class="message">{message}</div>
				{/if}
				<div class="table-content">
					<div class="table-header-row">
						<span class="table-header-text">{tableHeader}</span>
						<span class="selection-count">({selectedCount}) {selectedCountSuffix}</span>
					</div>
					<div class="divider"></div>
					<div class="items-list">
						{#each items as item (item.id)}
							<div
								class="item-row"
								onclick={() => toggleItem(item.id, !selectedIds.includes(item.id))}
							>
								<Checkbox
									id={item.id}
									labelFontProps={checkboxLabelFontProps}
									checked={selectedIds.includes(item.id)}
								/>
								<span class="item-label">
									{item.label}
								</span>
							</div>
						{/each}
					</div>
				</div>
			</div>
			<div class="actions">
				<SecondaryButton
					label={$t('UI.button.cancel')}
					color={SecondaryBtnColors.GREEN}
					on:click={() => modals.close()}
					sizeAdaptingToText
				/>
				{#if acceptText && acceptText !== ''}
					<PrimaryButton
						label={acceptText}
						color={PrimaryBtnColors.GREEN}
						on:click={handleAccept}
						sizeAdaptingToText
						whiteIcon
					/>
				{/if}
			</div>
		</div>
	</div>
{/if}

<style lang="scss">
	@import '$styles/modals'; // also includes global.scss

	.header {
		@include roboto-font(normal, 500, 1.25rem);

		width: 100%;
		text-align: left;
	}

	.message {
		@include roboto-font(150%, 400, 0.875rem);

		padding-top: 7px;
		width: 100%;
		text-align: left;
	}

	.actual-content {
		width: 100%;

		@include flex-col;

		& p:first-child {
			color: #949494;

			@include roboto-font(1.125rem, 500, 0.75rem, #949494);

			text-align: center;
		}
	}

	@import '$styles/global';

	.table-content {
		width: 100%;
		margin-top: 1rem;
	}

	.table-header-row {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 0.5rem;
	}

	.table-header-text {
		@include roboto-font(normal, 500, 0.875rem);
	}

	.selection-count {
		@include roboto-font(normal, 400, 0.875rem, $primaryGreen);
	}

	.divider {
		width: 100%;
		height: 2px;
		background-color: $primaryGreen;
		margin-bottom: 0.25rem;
	}

	.items-list {
		padding-top: 0.625rem;
		max-height: 300px;
		overflow: hidden scroll;

		&::-webkit-scrollbar {
			width: 20px;
		}

		&::-webkit-scrollbar-track {
			box-shadow: inset 0 0 10px 10px $bluish-grey-200;
			border: solid 8px transparent;
			border-radius: 20px;
			background-clip: padding-box;
		}

		&::-webkit-scrollbar-thumb {
			box-shadow: inset 0 0 10px 10px $primaryGreen;
			border: solid 8px transparent;
			border-radius: 20px;
			background-clip: padding-box;
		}
	}

	.item-row {
		display: flex;
		flex-direction: row;
		gap: 12px;
		align-items: center;
		margin-bottom: 0.625rem;
		border-radius: 4px;
		cursor: pointer;

		&:hover {
			background-color: $bluish-grey-200;
		}
	}

	.item-label {
		@include roboto-font(1.25rem, 400, 0.875rem);
	}
</style>
